import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@/Components/shadcn/field';
import { RHFDefaultInput } from '@/Components/shadcn/rhf-default-input';
import { RHFNumericInput } from '@/Components/shadcn/rhf-numeric-input';
import { type InputProps } from '@/Components/shadcn/input';
import { cn } from '@/lib/utils';

export type RHFInputProps = Omit<InputProps, 'name' | 'value' | 'defaultValue' | 'onChange' | 'onBlur' | 'ref'> & {
    name: string;
    label?: ReactNode;
    hideLabel?: boolean;
    fullWidth?: boolean;
    displayValue?: string;
    inputOverlay?: ReactNode;
    fieldClassName?: string;
    fieldDataCy?: string;
    useNumberFormatter?: boolean;
    hideFieldError?: boolean;
};

function RHFInput({
    name,
    label,
    hideLabel,
    id,
    fullWidth,
    displayValue,
    inputOverlay,
    className,
    fieldClassName,
    type = 'text',
    fieldDataCy,
    useNumberFormatter,
    disabled,
    autoComplete = 'off',
    hideFieldError,
}: RHFInputProps) {
    const { formState } = useFormContext();
    const error = formState.errors[name];
    const inputId = id ?? name;

    return (
        <Field
            className={cn(fullWidth && 'w-full', fieldClassName)}
            data-invalid={error ? true : undefined}
            data-cy={fieldDataCy}
        >
            {label && !hideLabel ? (
                <FieldLabel htmlFor={inputId}>
                    {label}
                </FieldLabel>
            ) : null}
            <div className="relative">
                {useNumberFormatter ? (
                    <RHFNumericInput
                        name={name}
                        id={inputId}
                        className={className}
                        disabled={disabled}
                        autoComplete={autoComplete}
                    />
                ) : (
                    <RHFDefaultInput
                        name={name}
                        id={inputId}
                        type={type}
                        className={className}
                        displayValue={displayValue}
                        disabled={disabled}
                        autoComplete={autoComplete}
                    />
                )}
                {inputOverlay}
            </div>
            {!hideFieldError && <FieldError>{error?.message ? String(error.message) : null}</FieldError>}
        </Field>
    );
}

export { RHFInput };
